import React from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';
import Icon from './Icon';

function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 flex flex-col transition-colors duration-300">
      {/* Top Header with Logo and CTA Buttons */}
      <header className="sticky top-0 z-40 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-sm border-b border-zinc-200 dark:border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2 sm:gap-3">
              <Logo size="md" className="sm:hidden" />
              <Logo size="lg" className="hidden sm:block" />
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-100">
                FollowUP
              </h1>
            </div>
            
            {/* Right side: Theme Toggle and CTA Buttons */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* CTA Buttons - Visible on all screens */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/auth?mode=signin')}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/auth?mode=signup')}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded-lg font-semibold text-xs sm:text-sm transition-all shadow-md"
                >
                  Get Started
                </button>
              </div>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 sm:py-12">
        <div className="max-w-4xl w-full text-center space-y-6 sm:space-y-8">
          {/* Logo - Hidden on mobile, shown on larger screens for visual balance */}
          <div className="hidden md:flex justify-center mb-6 sm:mb-8">
            <div className="flex items-center gap-2 sm:gap-3">
              <Logo size="xl" />
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-zinc-900 dark:text-zinc-100">
                FollowUP
              </h1>
            </div>
          </div>

          {/* Main Tagline */}
          <div className="space-y-3 sm:space-y-4 px-2">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-zinc-900 dark:text-zinc-100 leading-tight px-2">
              Your network is your net worth
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-zinc-600 dark:text-zinc-400 font-light px-2">
              So never forget to{' '}
              <button
                onClick={() => navigate('/auth?mode=signup')}
                className="inline-block relative text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold underline decoration-2 underline-offset-4 transition-colors cursor-pointer"
                style={{
                  animation: 'twinkle 2s ease-in-out infinite',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.animation = 'none';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.animation = 'twinkle 2s ease-in-out infinite';
                }}
              >
                follow up
              </button>
            </p>
            <p className="text-base sm:text-lg md:text-xl text-zinc-500 dark:text-zinc-500 mt-4 sm:mt-6 px-2">
              We help you follow up more efficiently
            </p>
          </div>

          {/* Features Preview */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-12 max-w-3xl mx-auto px-4">
            <div className="bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 rounded-xl p-4 sm:p-6">
              <div className="text-2xl sm:text-3xl mb-2 sm:mb-3"><Icon emoji="📸" size="text-2xl sm:text-3xl" /></div>
              <h3 className="text-zinc-900 dark:text-zinc-100 font-semibold mb-2 text-sm sm:text-base">Scan Business Cards</h3>
              <p className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400">
                Instantly extract contact information with AI-powered OCR
              </p>
            </div>
            <div className="bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 rounded-xl p-4 sm:p-6">
              <div className="text-2xl sm:text-3xl mb-2 sm:mb-3"><Icon emoji="✉️" size="text-2xl sm:text-3xl" /></div>
              <h3 className="text-zinc-900 dark:text-zinc-100 font-semibold mb-2 text-sm sm:text-base">AI Follow-ups</h3>
              <p className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400">
                Get personalized follow-up email drafts tailored to each contact
              </p>
            </div>
            <div className="bg-zinc-50 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 rounded-xl p-4 sm:p-6 sm:col-span-2 md:col-span-1">
              <div className="text-2xl sm:text-3xl mb-2 sm:mb-3"><Icon emoji="📊" size="text-2xl sm:text-3xl" /></div>
              <h3 className="text-zinc-900 dark:text-zinc-100 font-semibold mb-2 text-sm sm:text-base">Manage Leads</h3>
              <p className="text-xs sm:text-sm text-zinc-600 dark:text-zinc-400">
                Organize and track all your networking contacts in one place
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-zinc-200 dark:border-zinc-800 py-4 sm:py-6 px-4">
        <div className="max-w-4xl mx-auto text-center text-zinc-500 dark:text-zinc-500 text-xs sm:text-sm">
          <p>© 2024 FollowUP. Making networking effortless.</p>
        </div>
      </div>
    </div>
  );
}

export default Landing;

